/**
 * Landing template: "SK Lodge" style — warm amber/orange, rounded cream
 * cards, bold sans headings, icon-tile amenities, photo-carousel hero.
 * Modelled on the reference site the user named (sklodge.in).
 *
 * Renders the SAME live tenant data the classic template does (settings,
 * rooms, reviews, FAQs) with the same honest empty states — no fabricated
 * placeholder content anywhere, same rule that governs LandingPage.jsx.
 */
import React, { useState, useEffect } from "react";
import {
  MapPin, Phone, Wifi, Wind, Droplets, Tv, Coffee, Star, Menu, X,
  BedDouble, Users, ArrowRight, ChevronLeft, ChevronRight, ChevronDown,
  ExternalLink, MessageCircle,
} from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import { toLocalISODate } from "../../utils/date";
import { imgSrc, useLandingData, RoomPhotoSlideshow, StarRow, ReviewLookupModal } from "./shared";

const AMENITIES = [
  { icon: Wifi, label: "High-Speed WiFi", desc: "Fibre broadband" },
  { icon: Wind, label: "Air Conditioning", desc: "All rooms AC" },
  { icon: Droplets, label: "24/7 Hot Water", desc: "Electric geyser" },
  { icon: Tv, label: "Smart TV", desc: "OTT streaming" },
  // Parking deliberately NOT listed here. This block is hardcoded copy shown
  // on EVERY tenant's landing page, so it claimed free parking in a secure
  // compound for properties that may have neither — and parking is exactly
  // the promise a guest arrives having relied on. It belongs to the room, not
  // the property: rooms carry real "2-Wheeler Parking" / "4-Wheeler Parking"
  // amenities the tenant sets themselves (RoomsPage.jsx's AMENITY_OPTIONS),
  // and those are what the room detail page shows.
  { icon: Coffee, label: "Kitchen Access", desc: "Fully equipped" },
];

const NAV_LINKS = [["Rooms", "rooms"], ["Amenities", "amenities"], ["Gallery", "gallery"], ["Reviews", "reviews"], ["Contact", "contact"]];

function Eyebrow({ children }) {
  return <p className="text-amber-600 text-xs font-bold uppercase tracking-[0.25em] text-center mb-2">{children}</p>;
}

function SectionHeading({ eyebrow, title }) {
  return (
    <div className="text-center mb-10">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900">{title}</h2>
    </div>
  );
}

export default function LandingPageSkLodge() {
  const {
    hotelName, address, phone, phone2, heroImages, aboutText, faqs,
    mapsUrl, mapsLat, mapsLng, crossPromoName, crossPromoUrl, googleReviewUrl, logoUrl,
  } = useSettings();
  const { rooms, bookingDisabled, liveReviews } = useLandingData();

  const [menuOpen, setMenuOpen] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  const [faqOpen, setFaqOpen] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [showReviewLookup, setShowReviewLookup] = useState(false);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");

  const gallery = (heroImages || []).map(imgSrc).filter(Boolean);
  const heroPhotos = gallery.length ? gallery : [];

  // Auto-advance the hero carousel only when there's more than one photo.
  useEffect(() => {
    if (heroPhotos.length < 2) return;
    const id = setInterval(() => setHeroIdx(i => (i + 1) % heroPhotos.length), 6000);
    return () => clearInterval(id);
  }, [heroPhotos.length]);

  const lowestPrice = rooms.length ? Math.min(...rooms.map(r => r.pricePerNight || Infinity)) : null;
  const avgRating = liveReviews.length
    ? (liveReviews.reduce((s, r) => s + (r.rating || 0), 0) / liveReviews.length).toFixed(1)
    : null;

  const bookHref = checkIn && checkOut
    ? `/book?checkIn=${checkIn}&checkOut=${checkOut}`
    : "/book";

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const waHref = phone ? `https://wa.me/${String(phone).replace(/\D/g, "")}` : null;

  return (
    <div className="min-h-screen bg-[#FFFBF3] text-slate-900">
      {/* ── Nav ── */}
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between gap-3">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-3 min-w-0">
            {logoUrl ? (
              <img src={imgSrc(logoUrl)} alt={hotelName}
                className="w-11 h-11 rounded-2xl object-cover flex-shrink-0 shadow-sm" />
            ) : (
              <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                <BedDouble size={20} className="text-white" />
              </span>
            )}
            <span className="min-w-0 text-left">
              <span className="block font-extrabold text-slate-900 leading-tight truncate">{hotelName}</span>
              {address && <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 truncate">{address}</span>}
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="text-sm font-medium text-slate-600 hover:text-amber-600 transition-colors">
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 flex-shrink-0">
            <a href="/login" className="hidden sm:inline text-xs text-slate-400 hover:text-slate-600 px-2">Admin</a>
            {!bookingDisabled && (
              <a href={bookHref} className="hidden sm:flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-bold text-white
                bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md shadow-orange-500/25 transition-all">
                Book Now <ArrowRight size={15} />
              </a>
            )}
            <button onClick={() => setMenuOpen(v => !v)} className="lg:hidden w-11 h-11 flex items-center justify-center rounded-xl hover:bg-amber-50">
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-amber-100 bg-white px-4 py-3 space-y-1">
            {NAV_LINKS.map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="w-full text-left px-3 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-amber-50">
                {label}
              </button>
            ))}
            <a href="/login" className="block px-3 py-3 rounded-xl text-sm font-medium text-slate-500 hover:bg-amber-50">Admin</a>
          </div>
        )}
      </header>

      {/* ── Hero ── */}
      <section className="relative">
        <div className="relative h-[78vh] min-h-[520px] overflow-hidden bg-slate-800">
          {heroPhotos.map((src, i) => (
            <img key={src + i} src={src} alt="" className="absolute inset-0 w-full h-full object-cover"
              style={{ opacity: i === heroIdx ? 1 : 0, transition: "opacity 1.2s ease" }} />
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/55 via-slate-900/35 to-slate-900/70" />

          {heroPhotos.length > 1 && (
            <>
              <button onClick={() => setHeroIdx(i => (i - 1 + heroPhotos.length) % heroPhotos.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/25 backdrop-blur flex items-center justify-center text-white hover:bg-white/40 transition-colors">
                <ChevronLeft size={20} />
              </button>
              <button onClick={() => setHeroIdx(i => (i + 1) % heroPhotos.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/25 backdrop-blur flex items-center justify-center text-white hover:bg-white/40 transition-colors">
                <ChevronRight size={20} />
              </button>
            </>
          )}

          <div className="relative h-full flex flex-col items-center justify-center text-center px-5">
            {!bookingDisabled && (
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur border border-white/25
                text-amber-300 text-[11px] font-bold uppercase tracking-[0.2em] mb-6">
                <Star size={12} className="fill-amber-300" /> Book Direct &amp; Save
              </span>
            )}
            <h1 className="text-4xl sm:text-6xl font-extrabold text-white leading-tight drop-shadow-lg">
              Welcome to<br />
              <span className="text-amber-400">{hotelName}</span>
            </h1>
            {address && <p className="mt-4 text-white/90 text-lg drop-shadow">{address}</p>}

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              {!bookingDisabled && (
                <a href={bookHref} className="flex items-center gap-2 px-7 py-4 rounded-full font-bold text-white
                  bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-xl shadow-orange-900/30 transition-all">
                  Book Your Stay <ArrowRight size={17} />
                </a>
              )}
              <button onClick={() => scrollTo("rooms")}
                className="px-7 py-4 rounded-full font-bold text-white bg-white/20 backdrop-blur border border-white/30 hover:bg-white/30 transition-colors">
                Explore Rooms
              </button>
            </div>
          </div>
        </div>

        {/* Availability card overlapping the hero */}
        {!bookingDisabled && (
          <div className="max-w-4xl mx-auto px-4 -mt-12 relative z-10">
            <div className="bg-white rounded-3xl shadow-xl border border-amber-100 p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Check In</label>
                <input type="date" value={checkIn} min={toLocalISODate(new Date())} onChange={e => setCheckIn(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Check Out</label>
                <input type="date" value={checkOut} min={checkIn || toLocalISODate(new Date())} onChange={e => setCheckOut(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
              <a href={bookHref} className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white
                bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md transition-all">
                Check Availability <ArrowRight size={16} />
              </a>
            </div>
          </div>
        )}
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 space-y-20">
        {/* ── Quick facts ── */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
          {avgRating && (
            <span className="flex items-center gap-1.5 font-semibold text-slate-800">
              <Star size={14} className="fill-amber-500 text-amber-500" /> {avgRating}
              <span className="font-normal text-slate-400">({liveReviews.length} review{liveReviews.length !== 1 ? "s" : ""})</span>
            </span>
          )}
          {!bookingDisabled && <span>{rooms.length} Room Type{rooms.length !== 1 ? "s" : ""}</span>}
          {lowestPrice !== null && lowestPrice !== Infinity && (
            <span>From <strong className="text-slate-800">₹{lowestPrice.toLocaleString("en-IN")}</strong>/night</span>
          )}
        </div>

        {/* ── About ── */}
        {aboutText && (
          <section className="max-w-3xl mx-auto text-center">
            <Eyebrow>Our Story</Eyebrow>
            <h2 className="text-3xl font-extrabold text-slate-900 mb-5">Why {hotelName}</h2>
            <p className="text-slate-600 leading-relaxed whitespace-pre-line">{aboutText}</p>
          </section>
        )}

        {/* ── Rooms ── */}
        <section id="rooms">
          <SectionHeading eyebrow="Stay With Us" title="Rooms &amp; Beds" />
          {bookingDisabled ? (
            <div className="rounded-3xl border border-dashed border-amber-200 bg-amber-50/40 py-14 text-center text-slate-500 px-6">
              Online booking is currently unavailable — please call {phone || "us"} to book directly.
            </div>
          ) : rooms.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-amber-200 bg-amber-50/40 py-14 text-center text-slate-400">
              Rooms coming soon
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rooms.map(room => {
                const photos = (room.photos || []).map(imgSrc).filter(Boolean);
                return (
                  <article key={room._id} className="group bg-white rounded-3xl overflow-hidden border border-amber-100 shadow-sm hover:shadow-xl transition-shadow">
                    <div className="relative h-56 bg-amber-50 overflow-hidden">
                      {photos.length ? <RoomPhotoSlideshow photos={photos} alt={room.name} />
                        : <div className="w-full h-full flex items-center justify-center text-amber-300"><BedDouble size={40} /></div>}
                    </div>
                    <div className="p-5">
                      <h3 className="font-extrabold text-lg text-slate-900">{room.name}</h3>
                      <p className="flex items-center gap-1.5 text-xs text-slate-400 mt-1">
                        <Users size={13} /> {room.baseOccupancy || room.capacity || 2} guests
                      </p>
                      {room.description && <p className="text-sm text-slate-500 mt-3 line-clamp-2">{room.description}</p>}
                      <div className="flex items-end justify-between mt-5">
                        <p className="text-2xl font-extrabold text-slate-900">
                          ₹{(room.pricePerNight || 0).toLocaleString("en-IN")}
                          <span className="text-xs font-medium text-slate-400"> /night</span>
                        </p>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <a href={`/rooms/${room._id}`} className="flex-1 text-center py-3 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors">
                          Details
                        </a>
                        <a href={`${bookHref}${bookHref.includes("?") ? "&" : "?"}roomId=${room._id}`}
                          className="flex-[1.4] flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-bold text-white
                            bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-sm transition-all">
                          Book This Room <ArrowRight size={14} />
                        </a>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Amenities ── */}
        <section id="amenities">
          <SectionHeading eyebrow="Comforts" title="Amenities" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {AMENITIES.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="bg-white rounded-3xl border border-amber-100 p-6 shadow-sm hover:shadow-md transition-shadow">
                <span className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
                  <Icon size={20} className="text-amber-600" />
                </span>
                <p className="font-bold text-slate-900">{label}</p>
                <p className="text-sm text-slate-400 mt-0.5">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Gallery ── */}
        <section id="gallery">
          <SectionHeading eyebrow="Photo Tour" title="Gallery" />
          {gallery.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-amber-200 bg-amber-50/40 py-14 text-center text-slate-400">
              Photos coming soon
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {gallery.map((src, i) => (
                <button key={src + i} onClick={() => setLightbox(src)}
                  className="relative aspect-[4/3] rounded-2xl overflow-hidden group">
                  <img src={src} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </button>
              ))}
            </div>
          )}
        </section>

        {/* ── Reviews ── */}
        <section id="reviews">
          <SectionHeading eyebrow="Guest Voices" title="Reviews" />
          {liveReviews.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-amber-200 bg-amber-50/40 py-14 text-center text-slate-400 px-6">
              No reviews yet — be the first to stay and share your experience.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {liveReviews.slice(0, 6).map((r, i) => (
                <div key={i} className="bg-white rounded-3xl border border-amber-100 p-6 shadow-sm">
                  <StarRow n={r.rating} />
                  <p className="text-slate-600 text-sm mt-3 leading-relaxed">{r.text}</p>
                  <div className="flex items-center gap-3 mt-5 pt-4 border-t border-amber-50">
                    <span className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 font-bold text-sm flex items-center justify-center">{r.avatar}</span>
                    <span>
                      <span className="block font-bold text-sm text-slate-900">{r.name}</span>
                      <span className="block text-xs text-slate-400">{r.date} · {r.via}</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="text-center mt-8">
            <button onClick={() => setShowReviewLookup(true)}
              className="px-6 py-3 rounded-full border border-amber-200 text-sm font-bold text-amber-700 hover:bg-amber-50 transition-colors">
              Write a Review
            </button>
          </div>
        </section>

        {/* ── FAQ ── */}
        {faqs && faqs.length > 0 && (
          <section>
            <SectionHeading eyebrow="Good to Know" title="Frequently Asked Questions" />
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((f, i) => (
                <div key={f._id || i} className="bg-white rounded-2xl border border-amber-100 overflow-hidden">
                  <button onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                    <span className="font-bold text-slate-900 text-sm">{f.question}</span>
                    <ChevronDown size={17} className={`text-amber-500 flex-shrink-0 transition-transform ${faqOpen === i ? "rotate-180" : ""}`} />
                  </button>
                  {faqOpen === i && <p className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{f.answer}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Cross-promo ── */}
        {crossPromoName && crossPromoUrl && (
          <section className="bg-slate-900 rounded-3xl p-8 sm:p-10 text-center">
            <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.25em] mb-3">Partner Service</p>
            <h3 className="text-2xl font-extrabold text-white mb-3">Planning a trip? Try {crossPromoName}</h3>
            <p className="text-slate-300 text-sm mb-6">Recommended by us — check them out for your trip.</p>
            <a href={crossPromoUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold text-slate-900 bg-amber-400 hover:bg-amber-300 transition-colors">
              Learn More <ArrowRight size={16} />
            </a>
          </section>
        )}

        {/* ── Contact ── */}
        <section id="contact" className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <p className="text-amber-600 text-xs font-bold uppercase tracking-[0.25em] mb-2">Find Us</p>
            <h2 className="text-3xl font-extrabold text-slate-900 mb-6">Location &amp; Contact</h2>
            <div className="space-y-3">
              {phone && (
                <a href={`tel:${phone}`} className="flex items-center gap-4 bg-white rounded-2xl border border-amber-100 p-4 hover:shadow-md transition-shadow">
                  <span className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0"><Phone size={18} className="text-green-600" /></span>
                  <span>
                    <span className="block font-bold text-slate-900">{phone}</span>
                    <span className="block text-xs text-slate-400">Call or WhatsApp</span>
                  </span>
                </a>
              )}
              {phone2 && (
                <a href={`tel:${phone2}`} className="flex items-center gap-4 bg-white rounded-2xl border border-amber-100 p-4 hover:shadow-md transition-shadow">
                  <span className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0"><Phone size={18} className="text-green-600" /></span>
                  <span className="block font-bold text-slate-900">{phone2}</span>
                </a>
              )}
              {address && (
                <a href={mapsUrl || (mapsLat && mapsLng ? `https://maps.google.com/?q=${mapsLat},${mapsLng}` : "#")}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-4 bg-white rounded-2xl border border-amber-100 p-4 hover:shadow-md transition-shadow">
                  <span className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0"><MapPin size={18} className="text-rose-500" /></span>
                  <span className="min-w-0">
                    <span className="block font-bold text-slate-900 truncate">{address}</span>
                    <span className="block text-xs text-slate-400">View on Google Maps</span>
                  </span>
                </a>
              )}
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              {!bookingDisabled && (
                <a href={bookHref} className="flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-white
                  bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md transition-all">
                  Book Now <ArrowRight size={16} />
                </a>
              )}
              {waHref && (
                <a href={waHref} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-green-700 bg-green-50 border border-green-200 hover:bg-green-100 transition-colors">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              )}
            </div>
          </div>

          {(mapsLat && mapsLng) || mapsUrl ? (
            <div className="bg-white rounded-3xl border border-amber-100 overflow-hidden shadow-sm">
              {mapsLat && mapsLng && (
                <iframe title="Map" className="w-full h-72 border-0" loading="lazy"
                  src={`https://maps.google.com/maps?q=${mapsLat},${mapsLng}&z=15&output=embed`} />
              )}
              <a href={mapsUrl || `https://maps.google.com/?q=${mapsLat},${mapsLng}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-4 text-sm font-bold text-amber-700 hover:bg-amber-50 transition-colors">
                <MapPin size={15} /> Open in Google Maps <ExternalLink size={13} />
              </a>
            </div>
          ) : null}
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="bg-slate-900 text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              {logoUrl ? (
                <img src={imgSrc(logoUrl)} alt={hotelName}
                  className="w-11 h-11 rounded-2xl object-cover flex-shrink-0" />
              ) : (
                <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  <BedDouble size={20} className="text-white" />
                </span>
              )}
              <span>
                <span className="block font-extrabold text-white">{hotelName}</span>
                {address && <span className="block text-[10px] uppercase tracking-[0.18em] text-slate-500">{address}</span>}
              </span>
            </div>
            {aboutText && <p className="text-sm text-slate-400 max-w-sm line-clamp-3">{aboutText}</p>}
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-white mb-3">Explore</p>
            <div className="space-y-2">
              {NAV_LINKS.map(([label, id]) => (
                <button key={id} onClick={() => scrollTo(id)} className="block text-sm text-slate-400 hover:text-amber-400 transition-colors">{label}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-white mb-3">Contact</p>
            <div className="space-y-2 text-sm text-slate-400">
              {phone && <p>📞 {phone}</p>}
              {address && <p>📍 {address}</p>}
              {googleReviewUrl && (
                <a href={googleReviewUrl} target="_blank" rel="noopener noreferrer" className="block text-amber-400 hover:text-amber-300">Review us on Google →</a>
              )}
              <a href="/login" className="block hover:text-amber-400">Admin Panel</a>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} {hotelName}
        </div>
      </footer>

      {/* ── Mobile sticky bar ── */}
      {!bookingDisabled && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-amber-100 px-4 py-3 flex items-center gap-3 safe-bottom">
          {waHref && (
            <a href={waHref} target="_blank" rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-green-50 border border-green-200 flex items-center justify-center flex-shrink-0">
              <Phone size={18} className="text-green-600" />
            </a>
          )}
          <a href={bookHref} className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-white
            bg-gradient-to-r from-amber-500 to-orange-500 shadow-md">
            Book Direct <ArrowRight size={16} />
          </a>
        </div>
      )}

      {/* ── Lightbox ── */}
      {lightbox && (
        <button onClick={() => setLightbox(null)} className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4">
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-xl" />
          <span className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 flex items-center justify-center text-white"><X size={20} /></span>
        </button>
      )}

      {showReviewLookup && (
        <ReviewLookupModal onClose={() => setShowReviewLookup(false)} />
      )}
    </div>
  );
}
